// Element-level entrances mirroring the source Elementor site: each item fades up from its own height
// (fadeInUp, 1.25s, delays stepping 100ms up to 400ms) and images wipe in from the reading-start edge
// (clip-path, 1.5s power2.out). Items already on screen when the page settles never animate.

const CASCADE_ITEM = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'dl', 'blockquote', 'figure', 'picture', 'img', 'video',
  'iframe', 'table', 'form', 'details', '.landing-button', '.landing-button-arrow', '.landing-card', '[data-motion-item]',
].join(', ');
const MEDIA = 'figure, picture, img, video';
const SKIPPED_BLOCKS = new Set(['hero', 'marquee']);

export function cascadeItems(section: HTMLElement, options: { skipHeadings: boolean; skipImages: boolean }) {
  return [...section.querySelectorAll<HTMLElement>(CASCADE_ITEM)].filter((element) => {
    const outer = element.parentElement?.closest(CASCADE_ITEM);
    if (outer && section.contains(outer)) return false;
    if (element.closest('[aria-hidden="true"]') || element.getClientRects().length === 0) return false;
    if (options.skipHeadings && element.matches('h1, h2')) return false;
    return !(options.skipImages && (element.matches(MEDIA) || element.querySelector('[data-motion-image]')));
  });
}

type Pending = { kind: 'cascade' | 'wipe'; section: HTMLElement; original: string };

export function createEntranceController(root: HTMLElement, view: Window & typeof globalThis) {
  const document = root.ownerDocument;
  const done = new WeakSet<Element>();
  const pending = new Map<HTMLElement, Pending>();
  const runs = new Set<Animation>();

  const wipeStart = (element: HTMLElement) => (view.getComputedStyle(element).direction === 'rtl' ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)');

  const settle = (element: HTMLElement, target: Pending) => {
    if (target.kind === 'cascade') element.style.opacity = target.original;
    else element.style.clipPath = target.original;
    pending.delete(element);
    done.add(element);
  };

  const observer = new view.IntersectionObserver((entries) => {
    const order = new Map<HTMLElement, number>();
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const element = entry.target as HTMLElement;
      observer.unobserve(element);
      const target = pending.get(element);
      if (!target) continue;
      settle(element, target);
      if (document.hidden || !element.animate) continue;
      const index = order.get(target.section) ?? 0;
      order.set(target.section, index + 1);
      try {
        const run = target.kind === 'cascade'
          ? element.animate([
            { opacity: 0, translate: '0 100%' },
            { opacity: view.getComputedStyle(element).opacity, translate: '0 0' },
          ], { duration: 1250, delay: Math.min(index * 100, 400), easing: 'ease', fill: 'backwards' })
          : element.animate([{ clipPath: wipeStart(element) }, { clipPath: 'inset(0 0 0 0)' }], { duration: 1500, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', fill: 'backwards' });
        runs.add(run);
        run.onfinish = () => { runs.delete(run); run.cancel(); };
      } catch {
        // The element is already visible; a failed animation must never hide content.
      }
    }
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });

  return {
    reconcile() {
      const next = new Map<HTMLElement, Omit<Pending, 'original'>>();
      for (const section of root.querySelectorAll<HTMLElement>('[data-block]')) {
        if (SKIPPED_BLOCKS.has(section.dataset.block ?? '')) continue;
        const wipe = section.dataset.imageReveal === 'wipe';
        if (wipe) for (const image of section.querySelectorAll<HTMLElement>('img[data-motion-image]')) next.set(image, { kind: 'wipe', section });
        if (section.dataset.elementMotion !== 'cascade') continue;
        for (const item of cascadeItems(section, { skipHeadings: section.dataset.motionHeading === 'letters', skipImages: wipe })) {
          if (!next.has(item)) next.set(item, { kind: 'cascade', section });
        }
      }
      for (const [element, target] of pending) {
        if (next.has(element)) continue;
        observer.unobserve(element);
        settle(element, target);
      }
      for (const [element, target] of next) {
        if (pending.has(element) || done.has(element)) continue;
        const rect = element.getBoundingClientRect();
        if (rect.top < view.innerHeight && rect.bottom > 0) {
          done.add(element);
          continue;
        }
        if (target.kind === 'cascade') {
          pending.set(element, { ...target, original: element.style.opacity });
          element.style.opacity = '0';
        } else {
          pending.set(element, { ...target, original: element.style.clipPath });
          element.style.clipPath = wipeStart(element);
        }
        observer.observe(element);
      }
    },
    revealWithin(node: Node) {
      for (const [element, target] of pending) {
        if (!element.contains(node)) continue;
        observer.unobserve(element);
        settle(element, target);
      }
    },
    stop() {
      observer.disconnect();
      runs.forEach((run) => run.cancel());
      runs.clear();
      for (const [element, target] of pending) settle(element, target);
    },
  };
}
